import type { ClassSummary } from '@school-erp/types';
import { createSubjectSchema } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';

interface Subject {
  id: string;
  name: string;
  code: string;
  subjectType: 'core' | 'elective' | 'co_curricular';
  isActive: boolean;
}
interface ClassSubject {
  subjectId: string;
  isOptional: boolean;
}

export function SubjectsTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', code: '', subjectType: 'core' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const subjects = useQuery({
    queryKey: ['subjects', schoolId],
    queryFn: () => api<Subject[]>(`/schools/${schoolId}/subjects`),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['subjects', schoolId] });

  const create = useMutation({
    mutationFn: (body: object) => api(`/schools/${schoolId}/subjects`, { method: 'POST', body }),
    onSuccess: () => {
      setForm({ name: '', code: '', subjectType: 'core' });
      setMessage(null);
      void refresh();
    },
    onError: (e) => setMessage(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/schools/${schoolId}/subjects/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (e) => setMessage(errorMessage(e)),
  });

  return (
    <section aria-label={t('acad.tab.subjects')} className="space-y-4">
      {message && <Notice kind="error">{message}</Notice>}
      {can('subjects.create', schoolId) && (
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = createSubjectSchema.safeParse(form);
            if (!parsed.success)
              return setErrors(
                Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])),
              );
            setErrors({});
            create.mutate(parsed.data);
          }}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
        >
          <TextField
            label={t('acad.name')}
            value={form.name}
            error={errors.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label={t('acad.code')}
            value={form.code}
            error={errors.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
          />
          <div>
            <label htmlFor="subject-type" className="block text-sm font-medium">
              {t('acad.type')}
            </label>
            <select
              id="subject-type"
              value={form.subjectType}
              onChange={(e) => setForm({ ...form, subjectType: e.target.value })}
              className={selectClass}
            >
              {['core', 'elective', 'co_curricular'].map((x) => (
                <option key={x} value={x}>
                  {t(`acad.${x}` as MessageKey)}
                </option>
              ))}
            </select>
          </div>
          <div className="self-end">
            <button type="submit" className={primaryButton}>
              {t('acad.addSubject')}
            </button>
          </div>
        </form>
      )}
      {subjects.data?.length === 0 && <p className="text-slate-600">{t('acad.noSubjects')}</p>}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {subjects.data?.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-2 p-3">
            <span>
              <span className="font-medium">{s.name}</span>{' '}
              <span className="font-mono text-xs text-slate-600">{s.code}</span>{' '}
              <span className="text-slate-600">· {t(`acad.${s.subjectType}` as MessageKey)}</span>
            </span>
            {can('subjects.delete', schoolId) && (
              <button
                type="button"
                className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                onClick={() =>
                  window.confirm(t('common.confirmDelete', { name: s.name })) && remove.mutate(s.id)
                }
              >
                {t('acad.delete')}
              </button>
            )}
          </li>
        ))}
      </ul>
      {subjects.data && subjects.data.length > 0 && (
        <ClassSubjects schoolId={schoolId} subjects={subjects.data} />
      )}
    </section>
  );
}

function ClassSubjects({ schoolId, subjects }: { schoolId: string; subjects: Subject[] }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const classes = useQuery({
    queryKey: ['classes', schoolId],
    queryFn: () => api<ClassSummary[]>(`/schools/${schoolId}/classes`),
  });
  const [classId, setClassId] = useState('');
  const [selected, setSelected] = useState<Record<string, boolean | undefined>>({}); // subjectId -> isOptional
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const current = useQuery({
    queryKey: ['class-subjects', schoolId, classId],
    queryFn: () => api<ClassSubject[]>(`/schools/${schoolId}/classes/${classId}/subjects`),
    enabled: !!classId,
  });
  useEffect(() => {
    if (current.data)
      setSelected(Object.fromEntries(current.data.map((s) => [s.subjectId, s.isOptional])));
  }, [current.data]);

  const save = useMutation({
    mutationFn: () =>
      api(`/schools/${schoolId}/classes/${classId}/subjects`, {
        method: 'PUT',
        body: {
          subjects: Object.entries(selected)
            .filter(([, v]) => v !== undefined)
            .map(([subjectId, isOptional]) => ({ subjectId, isOptional: !!isOptional })),
        },
      }),
    onSuccess: () => {
      setMessage({ kind: 'ok', text: t('common.saved') });
      void queryClient.invalidateQueries({ queryKey: ['class-subjects', schoolId, classId] });
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });
  const editable = can('subjects.update', schoolId);

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
      <div>
        <label htmlFor="cs-class" className="block text-sm font-medium">
          {t('acad.classSubjects')}
        </label>
        <select
          id="cs-class"
          value={classId}
          onChange={(e) => {
            setClassId(e.target.value);
            setMessage(null);
          }}
          className={selectClass}
        >
          <option value="" />
          {classes.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      {classId && (
        <>
          <ul className="grid gap-2 sm:grid-cols-2">
            {subjects
              .filter((s) => s.isActive)
              .map((s) => (
                <li key={s.id} className="flex items-center gap-3 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      disabled={!editable}
                      checked={selected[s.id] !== undefined}
                      onChange={(e) =>
                        setSelected((cur) => ({
                          ...cur,
                          [s.id]: e.target.checked ? false : undefined,
                        }))
                      }
                    />
                    {s.name}
                  </label>
                  {selected[s.id] !== undefined && (
                    <label className="flex items-center gap-1 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        disabled={!editable}
                        checked={selected[s.id] === true}
                        onChange={(e) =>
                          setSelected((cur) => ({ ...cur, [s.id]: e.target.checked }))
                        }
                      />
                      {t('acad.optional')}
                    </label>
                  )}
                </li>
              ))}
          </ul>
          {message && <Notice kind={message.kind}>{message.text}</Notice>}
          {editable && (
            <button
              type="button"
              className={secondaryButton}
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {t('acad.saveSubjects')}
            </button>
          )}
        </>
      )}
    </div>
  );
}
